import { Controller } from "@hotwired/stimulus"

// The app's own confirm, in place of the browser's.
//
// Turbo asks a single function whether to proceed, so replacing that one
// function replaces every data-turbo-confirm in the app at once — no call
// site changes, and nothing can be missed.
//
// The function must return a PROMISE resolving to true or false. Turbo waits
// on it, which is what lets a real dialog stand in for a blocking
// window.confirm.
export default class extends Controller {
  static targets = ["dialog", "message", "accept"]

  connect() {
    // Turbo.config.forms.confirm, not the top-level setConfirmMethod:
    // that one is deprecated in Turbo 8 and warns on every page load. It
    // is a straight alias for this assignment, so the behaviour is
    // unchanged and the warning goes.
    //
    // Guard against a second copy: the partial lives in the layout, so this
    // should be the only one, but registering twice would leave the first
    // dialog orphaned and waiting forever.
    if (window.Turbo?.config?.forms) {
      window.Turbo.config.forms.confirm = (message) => this.ask(message)
    }
  }

  ask(message) {
    this.messageTarget.textContent = message

    return new Promise((resolve) => {
      this.resolve = resolve
      this.dialogTarget.showModal()

      // Escape and the backdrop both close a <dialog> without our buttons
      // ever firing, and a promise nobody settles hangs the interaction
      // forever — the page would look frozen. `close` catches every route
      // out, whichever way it was taken.
      this.dialogTarget.addEventListener("close", () => this.settle(false),
                                         { once: true })
    })
  }

  accept() { this.settle(true) }
  cancel() { this.settle(false) }

  settle(answer) {
    // close() fires the listener above, so this runs twice for a button
    // press — once from the button, once from the close. The first answer is
    // the real one.
    if (!this.resolve) return

    const resolve = this.resolve
    this.resolve = null

    if (this.dialogTarget.open) this.dialogTarget.close()
    resolve(answer)
  }
}
